import { AnalysisRow } from '../DocumentAnalysisAPI';
import { nameOfPath } from '../real-gain/rag/Tree/storageTree';
import { truncateText } from '../utilities/fileFacts';

/**
 * Was über eine Datei im Datenraum bekannt ist.
 *
 * Der Datenraum zeigt die Ablage — Ordner und Dateien, wie sie liegen — und legt die
 * Dokumentenanalysen darüber. Die Verbindung ist die Id des verwalteten Dokuments, nicht der Pfad:
 * Eine Datei zu verschieben oder umzubenennen lässt ihren Befund unberührt, und das ist auch der
 * Grund, warum die Ablage die Struktur besitzen darf und die Analyse nur daran hängt.
 *
 * Eine Datei ohne Analyse ist kein Sonderfall. Sie ist eine gewöhnliche Datei — jemand hat sie über
 * den Dateimanager abgelegt — und wird als solche gezeigt und behandelt.
 */

export type DataRoomFileState =
    /** Keine Analyse: eine gewöhnliche Datei. */
    | 'plain'
    /** Ein Lauf ist unterwegs. */
    | 'running'
    /** Fertig, mit Befund. */
    | 'analysed'
    /** Der Lauf ist gescheitert. */
    | 'failed';

export interface DataRoomFile {
    state: DataRoomFileState;
    analysis?: AnalysisRow;
    /** Wahr, solange eine Analyse daran hängt — dann ist Löschen gesperrt. */
    locked: boolean;
}

/**
 * Der Zustand einer Datei aus ihrer Analyse.
 *
 * `locked` hängt an der bloßen Existenz einer Analyse und nicht an ihrem Ausgang: Auch ein
 * gescheiterter Lauf ist ein Befund über dieses Dokument, und ihn durch Löschen der Datei
 * verwaisen zu lassen wäre derselbe Verlust.
 */
export const dataRoomFileOf = (analysis: AnalysisRow | undefined): DataRoomFile => {
    if (!analysis) return { state: 'plain', locked: false };

    const status = String((analysis as any).status ?? '');

    return {
        state: status === 'running' ? 'running' : (status === 'failed' || status === 'aborted') ? 'failed' : 'analysed',
        analysis,
        locked: true,
    };
};

/**
 * Was in der Zeile steht — der erkannte Dokumenttyp, sonst nichts.
 *
 * Bewusst nichts statt „unbekannt": Eine Datei, deren Typ nicht feststeht, sagt darüber nichts aus,
 * und ein Wort an dieser Stelle läse sich wie ein Ergebnis.
 */
export const dataRoomFileLabel = (file: DataRoomFile): string | null => {
    const analysis: any = file.analysis;
    if (!analysis) return null;

    const role = String(analysis.roleName ?? '').trim();
    const subtype = String(analysis.subtypeName ?? '').trim();

    if (!role) return null;

    return subtype ? `${role} · ${subtype}` : role;
};

/**
 * Das Abzeichen einer Zeile — und damit die Antwort auf „Analyse oder gewöhnliche Datei?".
 *
 * Wo eines steht, hängt eine Dokumentenanalyse daran. Wo keines steht, hat jemand die Datei
 * abgelegt und sonst nichts. Das ist die Unterscheidung, die im Baum sofort lesbar sein muss —
 * ohne sie sieht ein noch nicht analysiertes Dokument aus wie ein fertig analysiertes.
 *
 * Der laufende Schritt kommt aus dem Fortschritt des Laufs, nicht aus der Analyse: Die Analyse
 * wird erst am Ende geschrieben, der Schritt ist das Einzige, was währenddessen etwas sagt.
 */
export interface DataRoomBadge {
    text: string;
    tone?: 'info' | 'ok' | 'warn' | 'error';
    busy?: boolean;
}

export const dataRoomBadge = (
    file: DataRoomFile,
    /** Der Schritt des laufenden Laufs, schon übersetzt. */
    runningStep: string | undefined,
    labels: { running: string; analysed: string; failed: string },
): DataRoomBadge | undefined => {
    switch (file.state) {
        // Kein Abzeichen: Genau daran erkennt man die gewöhnliche Datei.
        case 'plain':
            return undefined;
        case 'running':
            return { text: runningStep || labels.running, tone: 'info', busy: true };
        case 'failed':
            return { text: labels.failed, tone: 'error' };
        default:
            // Der erkannte Typ, wo er feststeht — er sagt mehr als das Wort „analysiert".
            return { text: dataRoomFileLabel(file) || labels.analysed, tone: 'ok' };
    }
};

/**
 * Wohin eine im Baum gezogene Datei kommt.
 *
 * Der Baum meldet den Wurf mit dem Zielordner — und mit einem **leeren** Pfad, wenn er auf die freie
 * Fläche daneben fiel. Diesen leeren Pfad wörtlich zu nehmen wäre der teure Fehler: Das Ziel wäre
 * die Wurzel der Mandantenablage, die Datei verließe den Projektordner, verschwände aus dem
 * Datenraum — und ihre Auswertung bliebe an ihr hängen, nur eben unsichtbar. Ohne Zielordner ist der
 * Projektordner gemeint.
 *
 * `null` heißt: nichts zu tun. Eine Datei zurück in den Ordner zu werfen, in dem sie schon liegt,
 * ist keine Bewegung, und der Server müsste sie sonst auf sich selbst schieben.
 */
export const moveDestination = (
    sourcePath: string,
    targetFolder: string,
    /** Der Projektordner — das Ziel, wenn keiner genannt ist. */
    projectFolder: string,
): string | null => {
    if (!sourcePath) return null;

    const target = targetFolder || projectFolder || '';
    const directory = !target || target.endsWith('/') ? target : `${target}/`;
    const destination = `${directory}${nameOfPath(sourcePath)}`;

    return destination === sourcePath ? null : destination;
};

/**
 * Wie viel von der Kurzfassung am Mauszeiger steht.
 *
 * Genug, um zu erkennen, worum es geht; zu wenig, um darin zu lesen. Wer mehr will, wählt die Datei
 * — dann steht die Kurzfassung vollständig bei den Angaben.
 */
export const SYNOPSIS_LENGTH = 200;

/**
 * Was am Mauszeiger über einer Zeile steht.
 *
 * Der Baum steht in einer schmalen Spalte, und ein Dateiname überlebt das selten ganz.
 * „Mietvertrag_Muell…" unterscheidet sich von „Mietvertrag_Mueller_2019…" nicht — der volle Name
 * ist deshalb keine Zugabe, sondern das Eigentliche. Die Kurzfassung dahinter entsteht ohnehin beim
 * Aufbereiten für die Suche und steht am Dokument; sie kostet hier nichts.
 *
 * Ohne Kurzfassung bleibt es beim Namen. Ein Hinweis mit einer leeren zweiten Zeile sähe aus, als
 * fehlte etwas.
 */
export const dataRoomHint = (path: string, summary: unknown): { title: string; text?: string } => {
    const text = truncateText(summary, SYNOPSIS_LENGTH);

    return text ? { title: nameOfPath(path), text } : { title: nameOfPath(path) };
};
